import { FontWeight } from '@nolimitcity/slot-launcher/bin/loader/font/FontStatics';
import { OpenSans } from '@nolimitcity/slot-launcher/bin/loader/font/OpenSans';
import { gsap } from 'gsap';
import { Beheaded } from '../Beheaded';
import { Helper } from '../../core/utils/Helper';
import { MathHelper } from '../../core/utils/MathHelper';
import { SpinePlayer } from '../../core/displayobject/SpinePlayer';
import { SymbolData } from '../../core/reel/symbol/config/SymbolData';
import { Sequence } from '../../core/displayobject/Sequence';
import { BLEND_MODES } from 'pixi.js';
import { SoundConfig } from '../config/sound/SoundConfig';

/**
 * Created by Jonas Wålekvist on 2023-02-14.
 * Updated by Sebastian Hovenberg
 */

export class LevelBar extends PIXI.Container {
    private levelThresholds: number[];

    private barBG: PIXI.Sprite;
    private barFill: PIXI.TilingSprite;

    private textField: PIXI.Text;
    private overFlowText: PIXI.Text;

    private _currentLevel: number = 0;
    private animatedLevelValue: number = 0;

    private _upgradeValue: number = 0;

    private barTimeline: gsap.core.Timeline;

    private maxWidth: number = 565; // Textures own Width.
    private collectedContainer: PIXI.Container;

    private fadeTextAlpha: number = 0.65; // Textures own Width.

    private currentBarWidth: number = 0;

    public get lvlTresholds(): number[] {
        return this.levelThresholds;
    }

    private get currentTreshold(): number {
        return this.levelThresholds[this._currentLevel];
    }

    public set currentBarLevel(val: number) {
        this._currentLevel = val;
    }

    public get currentBarLevel(): number {
        return this._currentLevel;
    }

    public get collectedValue(): number {
        return this.animatedLevelValue;
    }

    public get overflowedValue(): number {
        return Math.max(0, this.animatedLevelValue - this.currentTreshold);
    }

    constructor(levelThresholds: number[]) {
        super();
        this.levelThresholds = levelThresholds;
        this.init();
    }

    private get barSkinColor(): string {
        // Will be used when I put in the Loop stuff
        if (this.currentBarLevel < 2) {
            return 'green';
        } else if (this.currentBarLevel >= 2 && this.currentBarLevel < 4) {
            return 'purple';
        } else return 'red';
    }

    private fetchSpritePath(sym: string): string {
        if (sym == 'W') return sym;
        return sym.includes('L') ? sym : 'M_H' + sym[1];
    }

    private init() {
        // bar Spine
        // Animations: activate, deactivate  default loop loop_0 loop_static normal
        // Skins: green, purple , red
        // loading_bar

        this.barBG = this.addChild(new PIXI.Sprite(Beheaded.assetLoader.getTextures('loading_bar')[0]));
        this.barBG.position.set(280, -15);
        this.barBG.anchor.set(0.5);
        this.addChild(this.barBG);

        this.barFill = new PIXI.TilingSprite(
            Beheaded.assetLoader.getTextures(this.fetchBarColorTexturePath(this._currentLevel))[0],
            565,
            59
        );
        this.barFill.name = 'barFill';
        this.barFill.position.set(-6, -2);
        this.barFill.anchor.set(0, 0.5);
        this.barFill.width = 0;

        this.createText();

        this.addChild(this.barFill);
        this.addChild(this.textField, this.overFlowText);

        this.updateText(0);

        this.collectedContainer = this.addChild(new PIXI.Container());
        this.collectedContainer.y = 10;
    }

    public addHeadToBeCollected(symType: string, travelDurr: number = 0.3): void {
        let sym: SpinePlayer | PIXI.Sprite;
        if (symType.includes('H')) {
            sym = new SpinePlayer(this.fetchSpritePath(symType), true);
            sym.play({ animation: 'charged', loop: true });
        } else {
            sym = new PIXI.Sprite(SymbolData.getTextures(symType, 'NORMAL')![0]);
            sym.anchor.set(0.5);
        }
        this.collectedContainer.addChild(sym);

        const toPos = this.textField.x - this.textField.width;
        const startRandomRot = Math.random() * Math.PI;

        gsap.fromTo(sym.scale, { x: 0, y: 0 }, { x: 0.35, y: 0.35, duration: travelDurr });
        gsap.fromTo(
            sym,
            { rotation: startRandomRot },
            {
                x: toPos,
                rotation: startRandomRot + Math.PI * 2,
                ease: 'none',
                duration: travelDurr,
                onComplete: () => {
                    const item = Beheaded.overlayAnim.playStandAloneSequence(
                        Beheaded.assetLoader.getTextures('indicator_swosh'),
                        {},
                        this
                    ).item as Sequence;
                    item.position.x = toPos;
                    item.position.y = 10;
                    item.blendMode = BLEND_MODES.ADD;
                    item.scale.set(0.25);

                    (sym as PIXI.DisplayObject).destroy();
                },
            }
        );
    }

    private createText(): void {
        this.textField = new PIXI.Text('', {
            fontFamily: OpenSans.FAMILY,
            fontSize: 30,
            fontWeight: FontWeight.BOLD,
            dropShadow: true,
            dropShadowAlpha: 0.7,
            dropShadowAngle: 0,
            dropShadowBlur: 8,
            dropShadowColor: '#ffffff',
            dropShadowDistance: 0,
            fill: '#ffffff',
            miterLimit: 1,
            padding: '',
        });
        this.textField.blendMode = PIXI.BLEND_MODES.ADD;
        this.textField.position.set(281.5, 7);
        this.textField.anchor.set(0.5);
        this.textField.alpha = this.fadeTextAlpha;

        this.overFlowText = new PIXI.Text('', {
            fontFamily: OpenSans.FAMILY,
            fontSize: 20,
            fontWeight: FontWeight.BOLD,
            dropShadow: true,
            dropShadowAlpha: 0.7,
            dropShadowAngle: 0,
            dropShadowBlur: 8,
            dropShadowColor: '#ffffff',
            dropShadowDistance: 0,
            fill: '#ffffff',
            miterLimit: 1,
            padding: '',
        });
        this.overFlowText.blendMode = PIXI.BLEND_MODES.ADD;
        this.overFlowText.position.set(220, 13);
        this.overFlowText.anchor.set(0.5);
    }

    public setLevel(level: number): void {
        if (level == this.currentBarLevel) return;
        this.currentBarLevel = MathHelper.clamp(level, 0, 5);
        const reachedLastLevel = this.currentBarLevel == 5;

        this.textField.visible = !reachedLastLevel;
        this.overFlowText.visible = !reachedLastLevel;

        if (reachedLastLevel) {
            this.barFill.width = this.maxWidth;
            // this.barBG.play({ animation: "loop", skinType:"red", showOnEnd: true, loop:true });
        } else {
            // this.barBG.play({ animation: "deactivate", showOnEnd: true });
        }

        this.barFill.texture = Beheaded.assetLoader.getTextures(this.fetchBarColorTexturePath(this._currentLevel))[0];
    }

    private fetchBarColorTexturePath(level: number): string {
        if (level < 2) {
            return 'loading_bar_fill_green';
        } else if (level < 4) {
            return 'loading_bar_fill_purple';
        } else {
            return 'loading_bar_fill';
        }
    }

    public updateBarFillColor(level: number): void {
        const path = this.fetchBarColorTexturePath(level);
        this.barFill.texture = Beheaded.assetLoader.getTextures(path)[0];
    }

    forceReset(): void {
        this.animatedLevelValue = 0;
        this.barFill.width = 0;
        this.textField.text = `${this.animatedLevelValue} / ${this.currentTreshold}`;
        this.setLevel(0);
    }

    reset(level: number): void {
        this.currentBarWidth = 0;
        if (this.animatedLevelValue || this.currentBarLevel != level || this.barFill.width) {
            this.barTimeline = Helper.setTimeline(this.barTimeline);
            this.barTimeline.to([this.textField, this.barFill], { duration: 0.25, alpha: 0 }, 0);

            this.barTimeline.add(() => {
                this.setLevel(level);
                this.animatedLevelValue = 0;
                if (level != 5) this.barFill.width = 0;
                this.textField.text = `${this.animatedLevelValue} / ${this.currentTreshold}`;
            });

            this.barTimeline.to(this.barFill, { duration: 0.25, alpha: 1 }, 0.25);
            this.barTimeline.to(this.textField, { duration: 0.25, alpha: !level ? this.fadeTextAlpha : 1 }, 0.25);

            // if (this.currentBarLevel)
            //     this.barBG.play({ animation: "deactivate", showOnEnd: true });
        }
    }

    UpgradeToNextLevel() {
        this._upgradeValue = this.currentTreshold;
        const overFlowValue = this.overflowedValue;

        this.animatedLevelValue = 0;

        this.barTimeline = Helper.setTimeline(this.barTimeline, gsap.timeline({}));

        if (this.currentBarLevel < 4) {
            this.barTimeline.to(this.barFill, { duration: 0.25, alpha: 0 }, 0);
            this.barTimeline.to(
                this,
                {
                    duration: 0.25,
                    _upgradeValue: 0,
                    ease: 'none',
                    onUpdate: () => {
                        this.upgradeText(this._upgradeValue);
                    },
                },
                0
            );
            this.barTimeline.add(() => {
                this.currentBarLevel++;
                this.barFill.alpha = 1;
                this.barFill.width = 0;
            });

            const targetWidth =
                this.maxWidth * MathHelper.clamp(overFlowValue / this.levelThresholds[this.currentBarLevel + 1], 0, 1);

            this.barTimeline.to(this, {
                duration: 0.5,
                animatedLevelValue: Math.round(overFlowValue),
                ease: 'none',
                onUpdate: () => {
                    this.updateText();
                },
            });

            this.barTimeline.to(this.barFill, { duration: 0.5, width: targetWidth, ease: 'Back.easeOut(0.5)' }, '<');
        } else {
            this.barTimeline.to([this.barFill, this.textField], { duration: 0.25, alpha: 0 }, 0);
            this.barTimeline.add(() => {
                this.currentBarLevel++;
                this.barFill.texture = Beheaded.assetLoader.getTextures(this.fetchBarColorTexturePath(this._currentLevel))[0];
                // this.barBG.play({ animation: "loop", skinType:"red", showOnEnd: true, loop:true });
            });
            this.barTimeline.to(this.barFill, { duration: 0.25, alpha: 1 }, 0);
        }
        return this.barTimeline;
    }

    animateTo(targetValue: number, duration: number = 0.5, nextlevel: number) {
        if (this.currentBarLevel == 5) return;

        for (let i = this.currentBarLevel; i < nextlevel; i++) {
            targetValue += this.levelThresholds[i];
        }

        const targetWidth = this.maxWidth * MathHelper.clamp(targetValue / this.currentTreshold, 0, 1);

        this.barTimeline = Helper.setTimeline(
            this.barTimeline,
            gsap.timeline({
                onStart: () => {
                    if (targetWidth !== this.currentBarWidth) {
                        Beheaded.soundPlayer.playSound(SoundConfig.LVL_METER_COUNTER);
                    }
                },
                onUpdate: () => {
                    this.updateText();
                },
                onComplete: () => {
                    this.updateText(targetValue);
                    if (targetWidth !== this.currentBarWidth) {
                        Beheaded.soundPlayer.stopSound(SoundConfig.LVL_METER_COUNTER);
                        if (targetWidth <= this.maxWidth * 0.25) {
                            Beheaded.soundPlayer.playSound(SoundConfig.LVL_METER_UP_25);
                        } else if (targetWidth > this.maxWidth * 0.25 && targetWidth <= this.maxWidth * 0.5) {
                            Beheaded.soundPlayer.playSound(SoundConfig.LVL_METER_UP_50);
                        } else if (targetWidth > this.maxWidth * 0.5 && targetWidth <= this.maxWidth * 0.75) {
                            Beheaded.soundPlayer.playSound(SoundConfig.LVL_METER_UP_75);
                        } else if (targetWidth > this.maxWidth * 0.75) {
                            Beheaded.soundPlayer.playSound(SoundConfig.LVL_METER_UP_99);
                        }
                    }

                    this.currentBarWidth = targetWidth;
                },
            })
        );

        if (!this.currentBarLevel && !this.animatedLevelValue) {
            this.barTimeline.to([this.textField, this.barFill], { duration: duration / 2, alpha: 1 }, 0);
        }

        this.barTimeline.to(this.barFill, { duration: duration, width: targetWidth, ease: 'Back.easeOut(0.5)' }, 0);
        this.barTimeline.to(this, { duration: duration, animatedLevelValue: targetValue, ease: 'none' }, 0);

        return this.barTimeline;
    }

    upgradeText(levelValue: number) {
        levelValue = Math.round(levelValue);

        this.textField.text = `${levelValue} / ${this.currentTreshold}`;

        if (this.overFlowText.alpha)
            this.overFlowText.position.x = this.textField.position.x - (this.textField.width / 2 + this.overFlowText.width / 2);
    }

    updateText(levelValue: number = this.animatedLevelValue) {
        this.animatedLevelValue = Math.round(levelValue);

        if (this.animatedLevelValue > this.currentTreshold) {
            this.textField.text = `${this.currentTreshold} / ${this.currentTreshold}`;
            this.overFlowText.alpha = 1;
            this.overFlowText.text = `(+${this.animatedLevelValue - this.currentTreshold})`;
        } else {
            this.textField.text = `${this.animatedLevelValue} / ${this.currentTreshold}`;
            this.overFlowText.alpha = 0;
        }

        this.overFlowText.position.x = this.textField.position.x - (this.textField.width / 2 + this.overFlowText.width / 2);

        this.textField.alpha = !this.currentBarLevel && !this.animatedLevelValue ? this.fadeTextAlpha : 1;
    }
}
